import { forecastTiming } from "./forecastTiming";
import {
  futureSummary,
  historySummary,
  selectedHistory,
  statisticObservation,
  conversionObservation,
  measured,
} from "./teamAnalysis";
import { contextualMetrics } from "./teamWorkspace";
import { historicalHomeProfile, homeAdvantage } from "./teamSettingsImpact";
import { statisticSuggestion } from "./teamAdjustment";

export const guidedQuestions = [
  ["strength", "Spielstärke", "Strength"],
  ["goals", "Torniveau", "Goal level"],
  ["home", "Heimvorteil", "Home advantage"],
  ["draws", "Remisanteil", "Draw rate"],
  ["statistics", "Statistikmodell im Detail", "Statistics model in detail"],
];
// Timing describes provenance; it must not discard stored season comparison values.
export function beforeKickoff(game, source) {
  if (game.forecast_timing?.[source])
    return game.forecast_timing[source] === "prematch";
  return (
    forecastTiming(
      source === "statistics" ? { pre: game.statistics_model } : game.result,
      Date.parse(game.kickoff),
      source === "statistics"
    ) === "prematch"
  );
}
export function guidedPool(data, id) {
  return selectedHistory(data.observations || [], id);
}
const fields = ["home_for", "home_against", "away_for", "away_against"];
export function guidedAnalysis(
  data,
  id,
  { en = false, recent = 8, statField = "for_total", statName = (k) => k } = {}
) {
  const t = (d, e) => (en ? e : d);
  const fmt = (n, d = 3) =>
    measured(n)
      ? n.toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        })
      : "—";
  const sign = (n) => (measured(n) ? `${n > 0 ? "+" : ""}${fmt(n)}` : "—");
  const provenanceSummary = (pool, source) => {
    const available = pool.filter(g => historySummary([g], id, source).count);
    const timing = g => g.forecast_timing?.[source] || forecastTiming(
      source === "statistics" ? {pre:g.statistics_model} : g.result,
      Date.parse(g.kickoff), source === "statistics");
    const prematch = available.filter(g => timing(g) === "prematch").length;
    const replay = available.filter(g => timing(g) === "retrospective").length;
    const undated = available.length-prematch-replay;
    return t(
      `Herkunft: ${prematch} vor Anpfiff belegt, ${replay} nachträglich berechnet, ${undated} ohne belegten Berechnungszeitpunkt. Alle vorhandenen gespeicherten Werte sind einbezogen.`,
      `Provenance: ${prematch} documented before kickoff, ${replay} calculated retrospectively, ${undated} without a verified calculation time. All available stored values are included.`
    );
  };
  const games = guidedPool(data, id),
    all = selectedHistory(data.observations || [], id);
  const settings = {
    result: futureSummary(data.result_teams?.[id]),
    statistics: futureSummary(
      data.ranking?.find((r) => r.team_id === Number(id))
    ),
  };
  const previous = contextualMetrics(data, id, "previous", "all", false);
  const names = {
    result: t("Ergebnismodell", "Results model"),
    statistics: t("Statistikmodell", "Statistics model"),
    real: t("Tatsächliche Ergebnisse", "Actual results"),
    should: t(
      "Gemessene Leistung / Should Goals",
      "Measured performance / Should Goals"
    ),
    market: t("Markt", "Market"),
  };
  const period = (pool) =>
    pool.length
      ? `${pool.at(-1).kickoff.slice(0, 10)} – ${pool[0].kickoff.slice(0, 10)}`
      : t("Zeitraum fehlt", "Period unavailable");
  const coverage = (n, total = games.length) =>
    t(`${n} von ${total} Spielen`, `${n} of ${total} matches`);
  const unavailable = t(
    "Wert nicht verfügbar. Fehlende Daten sind keine Null.",
    "Value unavailable. Missing data are not zero."
  );
  const ratioNote = (s) =>
    s.count && s.against === 0
      ? t(
          "Keine Gegentore: ein endlicher Torquotient ist nicht definiert.",
          "No goals conceded: a finite goal ratio is undefined."
        )
      : unavailable;
  const cols = {
    home_for: t("Heim · Für", "Home · for"),
    home_against: t("Heim · Gegen", "Home · against"),
    away_for: t("Auswärts · Für", "Away · for"),
    away_against: t("Auswärts · Gegen", "Away · against"),
    for_total: t("Gesamt · Für", "Overall · for"),
    against_total: t("Gesamt · Gegen", "Overall · against"),
  };
  const currentNote = t(
    "Aktuelle Settings gelten für künftige Berechnungen. Heim und Auswärts sind gleich gewichtet.",
    "Current settings apply to future calculations. Home and away carry equal weight."
  );
  const subsetNote = t(
    "Verglichen werden genau dieselben Spiele. Gegner und Spielorte beeinflussen die Bilanz; daraus folgt noch kein neuer Settingwert.",
    "Exactly the same matches are compared. Opponents and venues affect the record; this does not determine a new setting."
  );
  const provenanceNote = t(
    "Gespeicherte Modellwerte aller verfügbaren Saisonspiele, einschließlich nachgepflegter Werte. Die Herkunft bleibt erhalten; keine Neuberechnung mit heutigen Settings.",
    "Stored model values for all available season matches, including backfilled values. Provenance is retained; no recalculation using current settings."
  );
  const summary = (key, pool = games) => historySummary(pool, id, key);
  const pair = (a, b, pool = games) =>
    pool.filter((g) => summary(a, [g]).count && summary(b, [g]).count);
  const archived = {
    count: previous.count,
    for: previous.goalsFor,
    against: previous.goalsAgainst,
    for_average: previous.count ? previous.goalsFor / previous.count : null,
    against_average: previous.count
      ? previous.goalsAgainst / previous.count
      : null,
    quotient: previous.quotient,
    goal_sum:
      previous.count && measured(previous.goalTotal)
        ? previous.goalTotal / previous.count
        : null,
  };
  const comparisons = (key, metric, pool) => {
    const refs =
      key === "real"
        ? []
        : key === "should"
        ? ["real", "statistics"]
        : key === "market"
        ? ["real", "should"]
        : ["real"];
    return refs.map((ref) => {
      const common = pair(key, ref, pool),
        a = summary(key, common),
        b = summary(ref, common);
      return {
        label: `${names[key]} − ${names[ref]}`,
        count: common.length,
        left: a[metric],
        right: b[metric],
        delta:
          measured(a[metric]) && measured(b[metric])
            ? a[metric] - b[metric]
            : null,
        detail: t(
          `Tordifferenz je Spiel: ${sign(
            common.length
              ? (a.for - a.against - b.for + b.against) / common.length
              : null
          )}.`,
          `Goal difference per match: ${sign(
            common.length
              ? (a.for - a.against - b.for + b.against) / common.length
              : null
          )}.`
        ),
      };
    });
  };
  const scalar = (question) => {
    const strength = question === "strength",
      metric = strength ? "quotient" : "goal_sum",
      settingField = strength ? "total_strength" : "total_goals",
      unit = strength
        ? t("Spielstärke · Quotient", "Strength · ratio")
        : t("Torniveau · Tore pro Spiel", "Goal level · goals per match");
    const saved = ["result", "statistics"].map((model, i) => {
      const s = settings[model],
        value = s?.[settingField];
      const details = [
        currentNote,
        s
          ? t(
              `Für ${fmt(s.for_total)}, Gegen ${fmt(s.against_total)}. ${
                strength ? "Quotient = Für / Gegen" : "Torschnitt = Für + Gegen"
              }.`,
              `For ${fmt(s.for_total)}, against ${fmt(s.against_total)}. ${
                strength
                  ? "Ratio = for / against"
                  : "Goals per match = for + against"
              }.`
            )
          : unavailable,
      ];
      // Illustration uses a known previous season length, never assumes 34 games.
      if (s && previous.count > 0)
        details.push(
          t(
            `Nur zur Veranschaulichung über ${
              previous.count
            } Spiele (Länge der verfügbaren Vorsaison): ${fmt(
              s.for_total * previous.count,
              1
            )} − ${fmt(
              s.against_total * previous.count,
              1
            )} Tore. Das ist keine Saisonprognose; der tatsächliche Spielplan ist hier nicht eingerechnet.`,
            `Illustration over ${
              previous.count
            } matches (available previous season length): ${fmt(
              s.for_total * previous.count,
              1
            )} − ${fmt(
              s.against_total * previous.count,
              1
            )} goals. This is not a season forecast; the actual schedule is not included.`
          )
        );
      return {
        key: `saved-${model}`,
        title: `${names[model]} · ${t(
          "aktuell gespeichert",
          "currently saved"
        )}`,
        value,
        unit,
        compact: i
          ? t(
              `Dies ist die zusammengefasste ${
                strength ? "Spielstärke" : "Torerwartung"
              } des Statistikmodells. Abstand zum Ergebnismodell: ${sign(
                measured(value) && measured(settings.result?.[settingField])
                  ? value - settings.result[settingField]
                  : null
              )}. Die Einzelstatistiken untersuchen wir separat.`,
              `This is the statistics model's combined ${
                strength ? "strength" : "goal expectation"
              }. Difference from the results model: ${sign(
                measured(value) && measured(settings.result?.[settingField])
                  ? value - settings.result[settingField]
                  : null
              )}. Individual statistics are examined separately.`
            )
          : t(
              strength
                ? "Die gespeicherte Spielstärke ist der Quotient aus erwarteten eigenen Toren und Gegentoren."
                : "Das gespeicherte Torniveau ist die Summe aus erwarteten eigenen Toren und Gegentoren.",
              strength
                ? "Saved strength is the ratio of expected goals for to goals against."
                : "Saved goal level is expected goals for plus goals against."
            ),
        details,
        coverage: t(
          "Gespeicherter Zustand, keine Spielbilanz",
          "Saved state, not a match record"
        ),
      };
    });
    const historical = (key, pool = games, title = names[key]) => {
      const s = summary(key, pool),
        value = s[metric];
      const details = [
        t(
          `Für ${fmt(s.for, key === "real" ? 0 : 3)}, Gegen ${fmt(
            s.against,
            key === "real" ? 0 : 3
          )} aus ${s.count} Spielen. Pro Spiel: ${fmt(
            s.for_average
          )} für, ${fmt(s.against_average)} gegen.`,
          `For ${fmt(s.for, key === "real" ? 0 : 3)}, against ${fmt(
            s.against,
            key === "real" ? 0 : 3
          )} in ${s.count} matches. Per match: ${fmt(s.for_average)} for, ${fmt(
            s.against_average
          )} against.`
        ),
        subsetNote,
      ];
      if (["result", "statistics"].includes(key)) details.push(provenanceNote);
      if (
        key === "market" &&
        pool.some((g) => g.market && g.market.quality !== "verified")
      )
        details.push(
          t(
            "Ein Teil der Marktrückrechnungen ist als ungeprüft / angenähert markiert.",
            "Some market inversions are marked unverified / approximate."
          )
        );
      if (key === "real" && previous.count > 0)
        details.push(
          t(
            `Vorsaison: ${fmt(archived.for_average)} Tore und ${fmt(
              archived.against_average
            )} Gegentore pro Spiel. Aktuell ${fmt(s.for_average)} und ${fmt(
              s.against_average
            )}. ${
              s.for_average < archived.for_average
                ? "Die Offensive erzielt weniger Tore pro Spiel."
                : "Die Offensive erzielt mindestens ebenso viele Tore pro Spiel."
            } ${
              s.against_average < archived.against_average
                ? "Die Defensive kassiert weniger Gegentore pro Spiel."
                : "Die Defensive kassiert mindestens ebenso viele Gegentore pro Spiel."
            }`,
            `Previous season: ${fmt(archived.for_average)} goals for and ${fmt(
              archived.against_average
            )} against per match. Currently ${fmt(s.for_average)} and ${fmt(
              s.against_average
            )}. ${
              s.for_average < archived.for_average
                ? "Fewer goals scored per match."
                : "At least as many goals scored per match."
            } ${
              s.against_average < archived.against_average
                ? "Fewer goals conceded per match."
                : "At least as many goals conceded per match."
            }`
          )
        );
      const c = comparisons(key, metric, pool);
      const measuredComparison = c[0];
      const compact = !measured(value)
        ? ratioNote(s)
        : key === "real"
        ? t(
            `Die bisherige Bilanz ergibt ${fmt(
              value
            )}. Sie ist kein unmittelbar übernehmbarer Settingwert. ${
              s.count
            } Spiele können durch Gegner, Chancenverwertung und Zufall geprägt sein.`,
            `The record so far gives ${fmt(
              value
            )}. This is not a setting to copy directly. ${
              s.count
            } matches can be affected by opponents, finishing and chance.`
          )
        : key === "should"
        ? t(
            `Should Goals messen die statistische Leistung. ${
              measuredComparison?.count
                ? `Gegenüber den realen Ergebnissen liegt der Wert auf denselben Spielen um ${sign(
                    measuredComparison.delta
                  )} anders. Das ist ein Hinweis auf eine Ergebnisabweichung, kein Beweis für Glück, Pech oder eine bestimmte Ursache.`
                : "Für den Vergleich mit realen Ergebnissen fehlen gemeinsame Spiele."
            }`,
            `Should Goals measure statistical performance. ${
              measuredComparison?.count
                ? `Compared with actual results on the same matches, the difference is ${sign(
                    measuredComparison.delta
                  )}. This indicates outcome variance, not proof of luck, misfortune or a particular cause.`
                : "No shared matches for comparison with actual results."
            }`
          )
        : t(
            `Diese ${
              key === "market" ? "Markteinschätzung" : "bisherige Erwartung"
            } bezieht sich auf ${
              s.count
            } abgedeckte Saisonspiele. Vergleiche die Abweichung zu den tatsächlichen Ergebnissen auf genau derselben Spielmenge.`,
            `This ${
              key === "market" ? "market assessment" : "expectation"
            } covers ${
              s.count
            } season matches. Compare it with actual results on exactly the same matches.`
          );
      const baselineDifferences = ["result", "statistics"].map((model) => ({
        label: t(
          `Abstand zu gespeicherten Settings · ${names[model]}`,
          `Difference from saved settings · ${names[model]}`
        ),
        delta:
          measured(value) && measured(settings[model]?.[settingField])
            ? value - settings[model][settingField]
            : null,
      }));
      return {
        key,
        title,
        value,
        unit,
        compact: ["result", "statistics"].includes(key)
          ? compact + " " + provenanceSummary(pool, key)
          : compact,
        details,
        coverage: coverage(s.count, pool.length),
        period: period(pool),
        comparisons: c,
        baselineDifferences,
      };
    };
    return [
      ...saved,
      historical("real"),
      historical(
        "result",
        games,
        `${names.result} · ${t("bisherige Erwartung", "expectation so far")}`
      ),
      historical(
        "statistics",
        games,
        `${names.statistics} · ${t(
          "bisherige Erwartung",
          "expectation so far"
        )}`
      ),
      historical("should"),
      historical("market"),
      {
        key: "previous",
        title: t("Vorsaison", "Previous season"),
        value: archived[metric],
        unit,
        compact: previous.count
          ? t(
              `Die Vorsaison umfasst ${previous.count} Spiele mit ${fmt(
                previous.goalsFor,
                0
              )} − ${fmt(
                previous.goalsAgainst,
                0
              )} Toren. Sie ist ein anderer Zeitraum und kein unveränderter Maßstab für heute.`,
              `The previous season comprises ${
                previous.count
              } matches and ${fmt(previous.goalsFor, 0)} − ${fmt(
                previous.goalsAgainst,
                0
              )} goals. It is a different period, not an unchanged benchmark for today.`
            )
          : unavailable,
        coverage: coverage(previous.count, previous.count),
        period: previous.period,
        details: [
          t(
            `Pro Spiel: ${fmt(archived.for_average)} für, ${fmt(
              archived.against_average
            )} gegen.`,
            `Per match: ${fmt(archived.for_average)} for, ${fmt(
              archived.against_average
            )} against.`
          ),
          t(
            "Für die Vorsaison liegen hier nur die tatsächlichen Ergebnisse vor; keine belegten Erwartungen, Should Goals oder Marktwerte.",
            "Previous-season expectations, Should Goals and market values are not documented in this archive context."
          ),
        ],
      },
      {
        ...historical(
          "real",
          games.slice(0, recent),
          t(`Letzte ${recent} Spiele`, `Last ${recent} matches`)
        ),
        key: "recent",
        coverage: coverage(
          summary("real", games.slice(0, recent)).count,
          recent
        ),
        compact: t(
          `Die letzten ${Math.min(
            recent,
            games.length
          )} verfügbaren Spiele sind Teil der Saison, keine unabhängige Bestätigung. Kleine Stichproben schwanken stärker.`,
          `The latest ${Math.min(
            recent,
            games.length
          )} available matches are part of the season, not independent confirmation. Small samples fluctuate more.`
        ),
      },
    ];
  };
  const homeSteps = ["result", "statistics"].map((model) => ({
    key: `saved-${model}`,
    title: `${names[model]} · ${t("aktuell gespeichert", "currently saved")}`,
    unit: t("Heimvorteil · Quotient", "Home advantage · ratio"),
    value: homeAdvantage(settings[model]),
    compact: t(
      "Offensiver Heimfaktor geteilt durch defensiven Heimfaktor. Größer als eins bedeutet einen Vorteil der Heimwerte gegenüber den Auswärtswerten.",
      "Attacking home factor divided by defensive home factor. Above one indicates a home advantage relative to away values."
    ),
    details: [
      t(
        `Offensiv ${fmt(settings[model]?.for_homefactor)} / defensiv ${fmt(
          settings[model]?.against_homefactor
        )}.`,
        `Attacking ${fmt(settings[model]?.for_homefactor)} / defensive ${fmt(
          settings[model]?.against_homefactor
        )}.`
      ),
    ],
    coverage: currentNote,
  }));
  for (const key of ["real", "result", "statistics", "should", "market"]) {
    const s = historicalHomeProfile(games, id, key);
    homeSteps.push({
      key,
      title:
        names[key] +
        (["result", "statistics"].includes(key)
          ? ` · ${t("bisherige Erwartung", "expectation so far")}`
          : ""),
      unit: t("Heimvorteil · Quotient", "Home advantage · ratio"),
      value: s.advantage,
      coverage: t(
        `${s.home.count} Heimspiele / ${s.away.count} Auswärtsspiele`,
        `${s.home.count} home / ${s.away.count} away matches`
      ),
      period: period(games),
      compact: measured(s.advantage)
        ? t(
            "Aus Heim- und Auswärtswerten mit derselben vorhandenen Quotientenberechnung gebildet. Wenige Spiele und unterschiedliche Gegner erlauben keine aggressive Anpassung.",
            "Uses the same existing ratio calculation on home and away values. Few matches and different opponents do not justify aggressive changes."
          )
        : t(
            "Für diesen Vergleich fehlen verwertbare Heim- oder Auswärtswerte.",
            "Usable home or away values are missing."
          ),
      details: [
        t(
          `Heim pro Spiel ${fmt(s.home.for_average)} für / ${fmt(
            s.home.against_average
          )} gegen. Auswärts ${fmt(s.away.for_average)} für / ${fmt(
            s.away.against_average
          )} gegen.`,
          `Home per match ${fmt(s.home.for_average)} for / ${fmt(
            s.home.against_average
          )} against. Away ${fmt(s.away.for_average)} for / ${fmt(
            s.away.against_average
          )} against.`
        ),
        ...(["result", "statistics"].includes(key) ? [provenanceNote] : []),
      ],
    });
  }
  const ph = contextualMetrics(data, id, "previous", "home"),
    pa = contextualMetrics(data, id, "previous", "away");
  homeSteps.push({
    key: "previous",
    title: t("Vorsaison · Heimvorteil", "Previous season · home advantage"),
    value: homeAdvantage({
      for_home: ph.count ? ph.goalsFor / ph.count : null,
      against_home: ph.count ? ph.goalsAgainst / ph.count : null,
      for_away: pa.count ? pa.goalsFor / pa.count : null,
      against_away: pa.count ? pa.goalsAgainst / pa.count : null,
    }),
    unit: t("Heimvorteil · Quotient", "Home advantage · ratio"),
    coverage: t(
      `${ph.count} Heimspiele / ${pa.count} Auswärtsspiele`,
      `${ph.count} home / ${pa.count} away matches`
    ),
    compact: t(
      "Eigener Zeitraum; dieselbe Heim-/Auswärtsberechnung.",
      "Separate period; same home/away calculation."
    ),
    details: [],
    period: previous.period,
  });
  homeSteps.push({
    key: "league",
    title: t(
      "Liga · gespeicherter Heimvorteil",
      "League · saved home advantage"
    ),
    value:
      Number.isFinite(data.result_parameters?.exp_home_factor) &&
      data.result_parameters.exp_home_factor < 2
        ? data.result_parameters.exp_home_factor /
          (2 - data.result_parameters.exp_home_factor)
        : null,
    unit: t("Heimvorteil · Quotient", "Home advantage · ratio"),
    compact: t(
      "Gespeicherter Liga-Heimwert geteilt durch den Auswärtswert (2 minus Heimwert). Beide Faktoren ergeben zusammen 2.",
      "Saved league home factor divided by the away factor (2 minus home factor). Both factors add up to 2."
    ),
    details: [
      t(
        "Kein belegter übergreifender Referenzwert in dieser Quotientendefinition vorhanden.",
        "No documented broader reference in this ratio definition available."
      ),
    ],
  });
  const drawSteps = ["result", "statistics"].map((key) => {
    const pool = games.filter((g) => summary(key, [g]).outcomes),
      s = summary(key, pool).outcomes,
      a = summary("real", pool).outcomes;
    return {
      key,
      title: `${names[key]} · ${t("erwartete Remisen", "expected draws")}`,
      value: s ? (100 * s.draw) / s.count : null,
      unit: t("Remisanteil · %", "Draw rate · %"),
      coverage: coverage(s?.count || 0),
      period: period(pool),
      compact: s
        ? t(
            `${fmt(s.draw, 1)} Remis erwartet. Auf denselben Spielen sind ${
              a?.draw ?? "—"
            } Remis eingetreten (${fmt(
              a ? (100 * a.draw) / a.count : null,
              1
            )} %).`,
            `Expected ${fmt(s.draw, 1)} draws. The same matches produced ${
              a?.draw ?? "—"
            } draws (${fmt(a ? (100 * a.draw) / a.count : null, 1)}%).`
          )
        : unavailable,
      details: [
        `${t(
          "Faire Remisquote erwartet / tatsächlich",
          "Fair draw odds expected / actual"
        )}: ${fmt(s?.draw > 0 ? s.count / s.draw : null)} / ${fmt(
          a?.draw > 0 ? a.count / a.draw : null
        )}.`,
        provenanceNote,
        t(
          "Remisfaktor und Remisanteil sind unterschiedliche Größen. Niedrigerer Faktor bedeutet mehr Remisen bei sonst gleichen Werten; der Faktor ist kein Prozentsatz.",
          "Draw factor and draw rate are different quantities. A lower factor means more draws with other values unchanged; the factor is not a percentage."
        ),
      ],
      comparisons: [
        {
          label: t("Tatsächlich − erwartet", "Actual − expected"),
          left: a ? (100 * a.draw) / a.count : null,
          right: s ? (100 * s.draw) / s.count : null,
          delta: s && a ? 100 * (a.draw / a.count - s.draw / s.count) : null,
          count: pool.length,
        },
      ],
    };
  });
  const actualDraws = summary("real").outcomes;
  drawSteps.push({
    key: "real",
    title: t(
      "Tatsächliche Remisen · ganze Saison",
      "Actual draws · whole season"
    ),
    value: actualDraws ? (100 * actualDraws.draw) / actualDraws.count : null,
    unit: t("Remisanteil · %", "Draw rate · %"),
    coverage: coverage(actualDraws?.count || 0),
    compact: t(
      `${
        actualDraws?.draw ?? "—"
      } Unentschieden. Der Remisfaktor soll träge reagieren. Aus wenigen Remisen wird keine direkte Zielgröße abgeleitet.`,
      `${
        actualDraws?.draw ?? "—"
      } draws. The draw factor should respond slowly. A few draws do not determine a target setting.`
    ),
    details: [],
  });
  const team = data.state?.teams?.[id],
    statSteps = [],
    proposals = [];
  const selected = games.filter(
    (g) =>
      statField.endsWith("total") ||
      g[statField.startsWith("home") ? "home_team_id" : "away_team_id"] ===
        Number(id)
  );
  const storedValue = (values, field = statField) =>
    field.endsWith("total")
      ? measured(
          values?.["home_" + (field === "for_total" ? "for" : "against")]
        ) &&
        measured(
          values?.["away_" + (field === "for_total" ? "for" : "against")]
        )
        ? (values["home_" + (field === "for_total" ? "for" : "against")] +
            values["away_" + (field === "for_total" ? "for" : "against")]) /
          2
        : null
      : values?.[field];
  const unitValue = (key, v) =>
    Number(key) === 45 && measured(v) && v > 1 ? v / 100 : v;
  for (const [key, values] of Object.entries(team?.statistics || {})) {
    const normalize = (pool) =>
      pool.map((g) => ({
        ...g,
        statistics: {
          ...g.statistics,
          [key]: Object.fromEntries(
            Object.entries(g.statistics?.[key] || {}).map(([s, v]) => [
              s,
              unitValue(key, v),
            ])
          ),
        },
      }));
    const expected = statisticObservation(selected, id, key, statField, true),
      observed = statisticObservation(normalize(selected), id, key, statField),
      percent = Number(key) === 45,
      display = (v) => (measured(v) && percent ? v * 100 : v);
    const paired = selected.filter(
        (g) =>
          statisticObservation([g], id, key, statField, true).count &&
          statisticObservation([g], id, key, statField).count
      ),
      pe = statisticObservation(paired, id, key, statField, true),
      po = statisticObservation(normalize(paired), id, key, statField);
    for (const basis of ["stored", "expected", "actual"]) {
      const value =
        basis === "stored"
          ? storedValue(values)
          : basis === "expected"
          ? expected.mean
          : observed.mean;
      statSteps.push({
        key: `stat-${key}-${basis}`,
        title: `${statName(key)} · ${cols[statField]} · ${t(
          basis === "stored"
            ? "aktuell gespeichert"
            : basis === "expected"
            ? "bisher erwartet"
            : "tatsächlich",
          basis === "stored"
            ? "currently saved"
            : basis === "expected"
            ? "expected so far"
            : "actual"
        )}`,
        value: display(value),
        unit: percent ? "%" : t("Pro Spiel", "Per match"),
        coverage:
          basis === "stored"
            ? currentNote
            : coverage(
                basis === "expected" ? expected.count : observed.count,
                selected.length
              ),
        period: basis === "stored" ? null : period(selected),
        compact:
          basis === "stored"
            ? t(
                "Dies ist der gespeicherte Eingangswert des Statistikmodells.",
                "This is the saved input value of the statistics model."
              )
            : basis === "expected"
            ? provenanceNote
            : t(
                `Auf ${
                  paired.length
                } gemeinsamen Spielen: tatsächlich minus erwartet ${sign(
                  paired.length ? display(po.mean) - display(pe.mean) : null
                )}. Dieser Unterschied ist noch keine Änderungsempfehlung.`,
                `On ${
                  paired.length
                } shared matches: actual minus expected ${sign(
                  paired.length ? display(po.mean) - display(pe.mean) : null
                )}. This difference is not yet an adjustment recommendation.`
              ),
        details: [
          ...fields.map((field) => {
            const e = statisticObservation(games, id, key, field, true),
              a = statisticObservation(normalize(games), id, key, field);
            return `${cols[field]}: ${t("gespeichert", "saved")} ${fmt(
              display(values[field])
            )}; ${t("erwartet", "expected")} ${fmt(display(e.mean))} (${
              e.count
            }/${e.selected}); ${t("tatsächlich", "actual")} ${fmt(
              display(a.mean)
            )} (${a.count}/${a.selected}).`;
          }),
          t(
            "Vorsaison-Einzelstatistiken sind in diesem Kontext nicht verfügbar.",
            "Previous-season individual statistics are unavailable in this context."
          ),
        ],
      });
    }
    for (const field of fields) {
      const p = statisticSuggestion(games, id, key, field, values[field]);
      if (p && Math.abs(p.value - values[field]) > 1e-10)
        proposals.push({
          key: `stat-${key}-${field}`,
          group: "statistic",
          team_id: Number(id),
          statistic_id: key,
          field,
          value: p.value,
          before: values[field],
          percent,
          title: `${statName(key)} · ${cols[field]}`,
          count: p.count,
          reason: t(
            `Tatsächlich ${fmt(display(p.observed))}, erwartet ${fmt(
              display(p.expected)
            )} aus ${
              p.count
            } gleichen Spielen. Bestehende vorsichtige Regel: n/(n+12), relative Änderung auf 20 % begrenzt. Ein Prüfvorschlag, keine gesicherte neue Teamstärke.`,
            `Observed ${fmt(display(p.observed))}, expected ${fmt(
              display(p.expected)
            )} from ${
              p.count
            } matched games. Existing cautious rule: n/(n+12), relative change limited to 20%. A review proposal, not a proven new team strength.`
          ),
        });
    }
  }
  if (!team)
    statSteps.push({
      key: "missing-statistics",
      title: names.statistics,
      value: null,
      compact: unavailable,
      details: [],
    });
  const conversionActual = conversionObservation(selected, id, statField),
    conversionExpected = conversionObservation(selected, id, statField, true);
  for (const basis of ["stored", "standard", "expected", "actual"]) {
    const v =
      basis === "stored"
        ? storedValue(team?.conversion)
        : basis === "standard"
        ? data.standard_conversion_rate
        : basis === "expected"
        ? conversionExpected.mean
        : conversionActual.mean;
    statSteps.push({
      key: `conversion-${basis}`,
      title: `Conversion Rate · ${t(
        basis === "stored"
          ? "aktuell gespeichert"
          : basis === "standard"
          ? "Standard"
          : basis === "expected"
          ? "bisher erwartet"
          : "tatsächlich",
        basis === "stored"
          ? "currently saved"
          : basis === "standard"
          ? "standard"
          : basis === "expected"
          ? "expected so far"
          : "actual"
      )}`,
      value: measured(v) ? 100 * v : null,
      unit: "%",
      coverage:
        basis === "stored" || basis === "standard"
          ? t("Modellparameter", "Model parameter")
          : coverage(
              basis === "expected"
                ? conversionExpected.count
                : conversionActual.count,
              selected.length
            ),
      compact: t(
        basis === "actual"
          ? "Tore geteilt durch die Summe der relevanten Attack Moments. Die tatsächlich erzielte Quote wird nicht vollständig als neues Setting übernommen."
          : basis === "expected"
          ? "Aus den gespeicherten erwarteten Toren und Attack Moments aller verfügbaren Spiele, einschließlich Nachpflege."
          : basis === "standard"
          ? "Der im vorhandenen Modell hinterlegte Standardwert."
          : "Die persönliche gespeicherte Conversion ist getrennt vom Standardwert.",
        basis === "actual"
          ? "Goals divided by total relevant Attack Moments. The observed rate is not copied in full into settings."
          : basis === "expected"
          ? "From stored expected goals and Attack Moments for all available matches, including backfills."
          : basis === "standard"
          ? "The standard value from the existing model."
          : "Personal saved conversion is separate from the standard."
      ),
      details: [
        t(
          "Conversion wird aus aufsummierten Toren / aufsummierten Attack Moments berechnet, nicht aus dem Durchschnitt einzelner Quoten. Die bestehenden Gewichte bleiben unverändert.",
          "Conversion uses pooled goals / pooled Attack Moments, not an average of individual rates. Existing weights remain unchanged."
        ),
        ...fields.map((field) => {
          const e = conversionObservation(games, id, field, true),
            a = conversionObservation(games, id, field);
          return `${cols[field]}: ${t("gespeichert", "saved")} ${fmt(
            measured(team?.conversion?.[field])
              ? team.conversion[field] * 100
              : null
          )} %; ${t("erwartet", "expected")} ${fmt(
            measured(e.mean) ? e.mean * 100 : null
          )} % (${e.count}/${e.selected}); ${t("tatsächlich", "actual")} ${fmt(
            measured(a.mean) ? a.mean * 100 : null
          )} % (${a.count}/${a.selected}); Season Parameter ${fmt(
            Number.isFinite(data.state?.reference?.conversion?.[field])
              ? data.state.reference.conversion[field] * 100
              : null
          )} %; ${t(
            "Tore / Attack Moments tatsächlich",
            "Actual goals / Attack Moments"
          )}: ${fmt(a.goals, 0)} / ${fmt(a.moments)}; ${t(
            "erwartet",
            "expected"
          )}: ${fmt(e.goals)} / ${fmt(e.moments)}.`;
        }),
      ],
    });
  }
  const sections = {
    strength: scalar("strength"),
    goals: scalar("goals"),
    home: homeSteps,
    draws: drawSteps,
    statistics: statSteps,
  };
  const conclusions = {};
  for (const key of ["strength", "goals"]) {
    const steps = sections[key],
      metric = key === "strength" ? "quotient" : "goal_sum",
      common = pair("real", "should"),
      real = summary("real", common),
      should = summary("should", common),
      difference =
        measured(real[metric]) && measured(should[metric])
          ? real[metric] - should[metric]
          : null;
    conclusions[key] = [
      t(
        `Aktuell gespeichert: ${names.result} ${fmt(steps[0].value)}, ${
          names.statistics
        } ${fmt(steps[1].value)}.`,
        `Currently saved: ${names.result} ${fmt(steps[0].value)}, ${
          names.statistics
        } ${fmt(steps[1].value)}.`
      ),
      ...steps
        .slice(2)
        .map((s) => `${s.title}: ${fmt(s.value)} (${s.coverage}).`),
      common.length
        ? t(
            `Auf denselben ${common.length} Spielen liegen Ergebnisse ${
              difference > 0 ? "über" : difference < 0 ? "unter" : "auf"
            } der gemessenen Leistung (Abstand ${sign(
              difference
            )}). Das beschreibt eine Abweichung, nicht ihre Ursache.`,
            `On the same ${common.length} matches, results are ${
              difference > 0 ? "above" : difference < 0 ? "below" : "level with"
            } measured performance (difference ${sign(
              difference
            )}). This describes a difference, not its cause.`
          )
        : t(
            "Für Ergebnis und Leistung fehlen gemeinsame Vergleichsspiele.",
            "No shared games for results and performance."
          ),
      t(
        "Ob die gespeicherte Einstellung richtig oder falsch ist, lässt sich aus diesen nicht gegnerbereinigten Quotienten allein nicht entscheiden. Eine geprüfte Regel für die Ableitung eines neuen Zielwerts fehlt. Deshalb hier keine automatische Anpassung.",
        "These ratios are not opponent-adjusted and cannot alone establish whether settings are right or wrong. A verified rule for deriving a new target is missing. No automatic adjustment is proposed."
      ),
    ];
  }
  conclusions.home = [
    ...homeSteps.map(
      (s) => `${s.title}: ${fmt(s.value)} · ${s.coverage || s.period || ""}`
    ),
    t(
      "Heim- und Auswärtswerte müssen auf genügend vergleichbaren Spielen beruhen. Eine geprüfte Regel für einen persönlichen Heimvorteils-Vorschlag fehlt. Keine automatische Änderung.",
      "Home and away values need sufficient comparable matches. A verified recommendation rule for individual home advantage is missing. No automatic change."
    ),
  ];
  conclusions.draws = [
    ...drawSteps.map((s) => `${s.title}: ${fmt(s.value)} % · ${s.compact}`),
    t(
      "Erwartete und tatsächliche Remisen sind oben auf derselben Spielmenge verglichen. Eine geprüfte Regel, die den Remisfaktor anhand dieser Abweichung vorsichtig anpasst, fehlt. Daher kein erfundener Zielwert.",
      "Expected and actual draws are compared above on the same matches. A verified rule for adjusting the factor slowly from this difference is missing. No invented target."
    ),
  ];
  conclusions.statistics = [
    t(
      `${proposals.length} Einzelwerte lassen sich am Ende mit der vorhandenen vorsichtigen Vorschlagsregel prüfen. Keine Übernahme während der Untersuchung.`,
      `${proposals.length} individual values can be reviewed at the end using the existing cautious proposal rule. Nothing is applied during the investigation.`
    ),
    t(
      "Conversion: Die vorhandene Vorschlagsregel berücksichtigt nicht alle geforderten Grundlagen (Standard, zusammengefasste Erwartung, tatsächliche Conversion und Vorsaison). Eine geprüfte vorsichtige Anpassungsregel, die all diese Grundlagen berücksichtigt, fehlt. Deshalb kein automatischer Conversion-Vorschlag; der Wert bleibt am Ende manuell bearbeitbar.",
      "Conversion: the existing proposal rule does not consider all required evidence (standard, pooled expectation, actual conversion and previous season). A verified cautious adjustment rule covering all this evidence is missing. No automatic conversion proposal; manual editing remains available at the end."
    ),
  ];
  return { sections, conclusions, proposals, settings, allCount: all.length };
}
